import type { DeploymentConfig } from "./contracts";
import { safely } from "./contracts";

export const FLAG = "CREATOR_WEB_OBSERVABILITY";
export const LEASE_MS = 5 * 60 * 1000;
export const POLL_MS = 4 * 60 * 1000;
export const CACHE_MS = 24 * 60 * 60 * 1000;
export const SEED_KEY = "wishlink.observability.seed.v1";
const CACHE_KEY = "wishlink.observability.decision.v1";
const MIN_REFRESH_MS = 30000;
const VARIANTS = {
  OFF: { percent: 0, trace: false },
  TELEMETRY_5: { percent: 5, trace: false },
  TELEMETRY_TRACE_5: { percent: 5, trace: true },
  TELEMETRY_100: { percent: 100, trace: false },
  TELEMETRY_TRACE_100: { percent: 100, trace: true },
};
type Variant = keyof typeof VARIANTS;
type Storage = Pick<globalThis.Storage, "getItem" | "setItem" | "removeItem">;
const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);
const variant = (name: unknown): name is Variant =>
  typeof name === "string" &&
  Object.prototype.hasOwnProperty.call(VARIANTS, name);

/** Ignore backend ids/weights; universal selection is server-owned. No name evaluation. */
export function parseVariant(body: unknown): Variant | undefined {
  if (!record(body) || body.success !== true || !record(body.data)) return;
  return body.data.is_enabled === true && variant(body.data.variant_name)
    ? body.data.variant_name
    : undefined;
}

/** No account input, logging or exported identifier. A corrupt seed stays ineligible. */
export function cohortBucket(
  storage: Storage,
  random: ((bytes: Uint8Array) => void) | undefined
): number | undefined {
  return safely(() => {
    if (!random) return undefined;
    let seed = storage.getItem(SEED_KEY);
    if (seed === null) {
      const bytes = new Uint8Array(16);
      random(bytes);
      seed = Array.from(bytes, (byte) =>
        byte.toString(16).padStart(2, "0")
      ).join("");
    }
    if (!/^[a-f0-9]{32}$/.test(seed)) return undefined;
    // Existing seeds also require usable storage; rewriting the same value never reassigns.
    storage.setItem(SEED_KEY, seed);
    if (storage.getItem(SEED_KEY) !== seed) return undefined;
    return (parseInt(seed.slice(0, 8), 16) / 0x100000000) * 100;
  }, undefined);
}
export const inCohort = (bucket: number | undefined, percent: number) =>
  bucket !== undefined &&
  Number.isFinite(bucket) &&
  bucket >= 0 &&
  bucket < percent;

export interface ActivationOptions {
  apiOrigin: string;
  scope: string;
  identity: DeploymentConfig["identity"];
  propagationAllowed: boolean;
  // Build adapter must know both construction opt-in and pinned SDK support.
  sdkPropagationSupported: boolean;
  storage: Storage;
  random?: (bytes: Uint8Array) => void;
  now(): number;
  monotonicNow(): number;
  visible(): boolean;
  fetch: typeof fetch;
  abort(): AbortController;
  schedule(ms: number, work: () => void): () => void;
  listen(work: () => void): () => void;
  controller: {
    configureDeployment(input: DeploymentConfig): Promise<boolean>;
    renewLease?(input: DeploymentConfig): boolean;
    disable(): void;
  };
}

/** SDK-free decision delivery. Starting it never awaits auth, rendering or business work. */
export function startRemoteActivation(options: ActivationOptions) {
  const o = options;
  const bucket = cohortBucket(o.storage, o.random);
  const scope = o.scope;
  let stopped = false;
  let serial = 0;
  let generation = 0;
  let failures = 0;
  let lastAttempt = -Infinity;
  let nextAttempt = -Infinity;
  let leaseEnd = -Infinity;
  let leaseRevision = 0;
  let leaseStart = 0;
  let leaseDuration = 0;
  let applied:
    | { key: string; config: DeploymentConfig; ready: boolean }
    | undefined;
  let pending = false;
  let abort: AbortController | undefined;
  let cancelPoll: (() => void) | undefined;
  let cancelLease: (() => void) | undefined;
  let cancelTimeout: (() => void) | undefined;
  const disable = () => {
    leaseRevision++;
    cancelLease?.();
    cancelLease = undefined;
    leaseEnd = -Infinity;
    applied = undefined;
    safely(() => o.controller.disable(), undefined);
  };
  const invalidate = () => {
    serial++;
    pending = false;
    abort?.abort();
    cancelTimeout?.();
    cancelTimeout = undefined;
  };
  const clearCache = () =>
    safely(() => o.storage.removeItem(CACHE_KEY), undefined);
  const leaseExpired = () =>
    o.now() >= leaseEnd ||
    o.now() < leaseEnd - leaseDuration ||
    o.monotonicNow() - leaseStart >= leaseDuration ||
    o.monotonicNow() < leaseStart;
  const armLease = (wallStart: number, monoStart: number) => {
    const expectedLease = ++leaseRevision;
    cancelLease?.();
    leaseStart = monoStart;
    leaseDuration = LEASE_MS;
    leaseEnd = wallStart + LEASE_MS;
    cancelLease = o.schedule(
      Math.max(
        0,
        Math.min(leaseEnd - o.now(), monoStart + LEASE_MS - o.monotonicNow())
      ),
      () => {
        if (expectedLease !== leaseRevision) return;
        invalidate();
        disable();
        // Local authority expires; the timestamped 24h hint belongs to future documents.
        nextAttempt = Math.max(
          failures ? nextAttempt : -Infinity,
          o.monotonicNow(),
          lastAttempt + MIN_REFRESH_MS
        );
        schedule();
      }
    );
  };
  const apply = (
    name: Variant,
    cached: boolean,
    wallStart: number,
    monoStart: number
  ) => {
    const policy = VARIANTS[name];
    if (!o.visible() || !inCohort(bucket, policy.percent)) {
      disable();
      return;
    }
    const propagationEnabled =
      !cached &&
      policy.trace &&
      o.propagationAllowed &&
      o.sdkPropagationSupported;
    const key = JSON.stringify([true, propagationEnabled, policy.percent]);
    const expiresAt = wallStart + LEASE_MS;
    if (applied && !leaseExpired() && applied.key === key) {
      // Same effective policy must not stop bootstrap, in-flight work or queued exports.
      // beta.1 cannot renew: retain its original deadline, never extend only local authority.
      if (applied.ready && expiresAt > leaseEnd && o.controller.renewLease) {
        const next = { ...applied.config, generation: ++generation, expiresAt };
        if (safely(() => o.controller.renewLease!(next), false)) {
          applied.config = next;
          armLease(wallStart, monoStart);
        }
      }
      return;
    }
    disable();
    const current = {
      key,
      ready: false,
      config: {
        generation: ++generation,
        enabled: true,
        eligible: true,
        exportEnabled: true,
        propagationEnabled,
        propagationEndpoints: ["getcreatorconfig", "getcreatordata"],
        expiresAt,
        timeoutMs: 30000,
        apiOrigins: [o.apiOrigin],
        identity: o.identity,
      },
    };
    applied = current;
    armLease(wallStart, monoStart);
    void Promise.resolve()
      .then(() => {
        if (stopped || applied !== current || leaseExpired()) return false;
        return o.controller.configureDeployment(current.config);
      })
      .then((enabled) => {
        if (applied !== current) return;
        if (!enabled) disable();
        else current.ready = true;
      })
      .catch(() => {
        if (applied === current) disable();
      });
  };
  const schedule = () => {
    cancelPoll?.();
    if (!stopped && o.visible())
      cancelPoll = o.schedule(
        Math.max(0, nextAttempt - o.monotonicNow()),
        refresh
      );
  };
  type Result = { kind: "decision"; name: Variant } | { kind: "unavailable" };
  function refresh(startup = false) {
    if (stopped || (!startup && !o.visible())) return;
    if (leaseEnd !== -Infinity && leaseExpired()) {
      invalidate();
      disable();
    }
    if (pending) return;
    if (o.monotonicNow() < nextAttempt) {
      schedule();
      return;
    }
    pending = true;
    lastAttempt = o.monotonicNow();
    nextAttempt = lastAttempt + MIN_REFRESH_MS;
    const expected = ++serial;
    const requestStartedAt = o.now();
    const requestMonoStart = lastAttempt;
    const unavailable: Result = { kind: "unavailable" };
    const off: Result = { kind: "decision", name: "OFF" };
    const request = async (): Promise<Result> => {
      abort = o.abort();
      const response = await o.fetch(
        `${o.apiOrigin}/api/c/featureFlags/getVariantForFeature/?feature_name=${FLAG}`,
        {
          method: "GET",
          credentials: "omit",
          redirect: "error",
          cache: "no-store",
          signal: abort.signal,
        }
      );
      // Server/network unavailability cannot extend authority, but need not discard a valid lease.
      if (
        response.status >= 500 ||
        response.status === 408 ||
        response.status === 429
      )
        return unavailable;
      if (response.status !== 200) return off;
      try {
        return {
          kind: "decision",
          name: parseVariant(await response.json()) ?? "OFF",
        };
      } catch (error) {
        return error instanceof SyntaxError ? off : unavailable;
      }
    };
    const timeout = new Promise<Result>((resolve) => {
      cancelTimeout = o.schedule(3000, () => {
        if (expected !== serial || !pending) return;
        abort?.abort();
        resolve(unavailable);
      });
    });
    void Promise.race([request().catch(() => unavailable), timeout]).then(
      (result) => {
        if (stopped || expected !== serial) return;
        pending = false;
        cancelTimeout?.();
        const age = o.monotonicNow() - requestMonoStart;
        const wallAge = o.now() - requestStartedAt;
        if (age < 0 || wallAge < 0) {
          disable();
          clearCache();
          result = unavailable;
        } else if (age >= 3000 || wallAge >= 3000) result = unavailable;
        failures = result.kind === "unavailable" ? failures + 1 : 0;
        nextAttempt =
          o.monotonicNow() +
          (failures
            ? Math.min(POLL_MS, MIN_REFRESH_MS * 2 ** Math.min(failures - 1, 3))
            : POLL_MS);
        if (result.kind === "unavailable") {
          if (leaseEnd !== -Infinity && leaseExpired()) disable();
        } else if (result.name === "OFF") {
          disable();
          clearCache();
        } else {
          // Persistence is only a startup hint for the next document. Fresh authority
          // still passes through eligibility and SDK lease checks if this write fails.
          safely(() => {
            o.storage.setItem(
              CACHE_KEY,
              JSON.stringify({
                version: 1,
                scope,
                name: result.name,
                fetchedAt: requestStartedAt,
              })
            );
          }, undefined);
          apply(result.name, false, requestStartedAt, requestMonoStart);
        }
        schedule();
      }
    );
  }
  const remove = o.listen(() => {
    if (stopped) return;
    if (!o.visible()) {
      cancelPoll?.();
      invalidate();
      disable();
      return;
    }
    if (leaseEnd !== -Infinity && leaseExpired()) {
      invalidate();
      disable();
    }
    // Initial pageshow/online on a healthy lease must not shorten the four-minute cadence.
    if (!applied && !failures)
      nextAttempt = Math.min(nextAttempt, lastAttempt + MIN_REFRESH_MS);
    refresh();
  });
  safely(() => {
    const raw = o.storage.getItem(CACHE_KEY);
    if (!raw || raw.length > 1024) return;
    const cached: unknown = JSON.parse(raw);
    if (
      record(cached) &&
      cached.version === 1 &&
      cached.scope === scope &&
      variant(cached.name) &&
      typeof cached.fetchedAt === "number" &&
      Number.isFinite(cached.fetchedAt) &&
      o.now() >= cached.fetchedAt &&
      o.now() - cached.fetchedAt <= CACHE_MS
    )
      apply(cached.name, true, o.now(), o.monotonicNow());
    else clearCache();
  }, undefined);
  refresh(true);
  return () => {
    stopped = true;
    generation++;
    invalidate();
    disable();
    cancelPoll?.();
    remove();
  };
}
